import { Observable } from 'rxjs'
import { normalize, schema } from 'normalizr'

import { request, parseAPIError, DownloadRequest } from '../../common/api'
import { companyNumberingSchema } from '../../common/normalizrSchemas'
import { normalizedEntitiesToRecordMap } from '../../common/helpers'

import * as actionTypes from './actionTypes'
import * as actionCreators from './actionCreators'
import * as tableModelActionTypes from '../../common/table/actionTypes'
import * as tableModelActionCreators from '../../common/table/actionCreators'
import * as companiesActionCreators from '../companies/actionCreators'
import { resolveModelState } from '../../common/table/helpers'
import { CompanyNumbering } from './model'
import { Company } from '../companies/model'

// Fetch

const fetchCompanyNumberingsEpic = (action$, store) => {
    const tableIdentifier = 'company_numberings_list'
    const serverSide = true

    const triggeringAction = serverSide
        ? action$.ofType(actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED)
        : action$.ofType(actionTypes.FETCH)

    return triggeringAction
        .filter(action => action.type !== tableModelActionTypes.CONFIGURATION_CHANGED || action.payload.tableIdentifier === tableIdentifier)
        .switchMap(action => {
            const modelState = resolveModelState(tableIdentifier, store.getState(), action)

            const requestParams = {
                method: 'GET',
                path: `company-numberings/list`,
            }

            if (serverSide) {
                requestParams.method = 'POST'
                requestParams.path += `?page=${modelState.getIn(['pagination', 'current']) + 1}`
                requestParams.body = {
                    sorting: modelState.get('sorting').toJS(),
                    filters: modelState.get('filters').toJS(),
                }
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_STARTED,
                }),
                request(requestParams)
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(
                            serverSide ? ajaxResponse.response.data : ajaxResponse.response,
                            new schema.Array(companyNumberingSchema)
                        )

                        const companyNumberings = normalizedEntitiesToRecordMap(
                            normalizedData.entities.company_numberings,
                            CompanyNumbering,
                            normalizedData.result
                        )
                        const companies = normalizedEntitiesToRecordMap(normalizedData.entities.companies, Company)

                        const observables = [
                            Observable.of(actionCreators.fetchCompanyNumberingsFulfilled(companyNumberings)),
                            Observable.of(companiesActionCreators.fetchCompaniesFulfilled(companies)),
                        ]

                        serverSide &&
                            observables.push(
                                Observable.of(
                                    tableModelActionCreators.updatePagination(
                                        tableIdentifier,
                                        ajaxResponse.response.last_page,
                                        ajaxResponse.response.current_page - 1,
                                        ajaxResponse.response.total
                                    )
                                )
                            )

                        return Observable.concat(...observables)
                    })
                    .catch(error => Observable.of(actionCreators.fetchCompanyNumberingFulfilled(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_CANCELLED, actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED))
            )
        })
}

// Fetch one

const fetchCompanyNumberingEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_ONE)
        .filter(() => !store.getState().companies.getIn(['current', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_ONE_STARTED,
                }),
                request({
                    path: `company-numberings/${action.payload}`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, companyNumberingSchema)

                        const companyNumberings = normalizedEntitiesToRecordMap(normalizedData.entities.company_numberings, CompanyNumbering)

                        return Observable.concat(
                            Observable.of(actionCreators.fetchCompanyNumberingsFulfilled(companyNumberings)),
                            Observable.of(actionCreators.fetchCompanyNumberingFulfilled())
                        )
                    })
                    .catch(error => Observable.of(actionCreators.fetchCompanyNumberingRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_ONE_CANCELLED))
            )
        )

// Save

const saveCompanyNumberingEpic = (action$, store) =>
    action$
        .ofType(actionTypes.SAVE)
        .filter(() => !store.getState().companies.getIn(['current', 'saving']))
        .switchMap(action => {
            const values = Object.assign({}, action.payload)

            let path = `company-numberings`
            let method = 'POST'

            if (values.id) {
                path = `company-numberings/${values.id}`
                method = 'PUT'

                delete values.id
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.SAVE_STARTED,
                }),
                request({
                    path,
                    method,
                    body: values,
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, companyNumberingSchema)

                        const companyNumberings = normalizedEntitiesToRecordMap(normalizedData.entities.company_numberings, CompanyNumbering)
                        const companyNumbering = companyNumberings.valueSeq().first()

                        return Observable.concat(Observable.of(actionCreators.saveCompanyNumberingFulfilled(companyNumbering)))
                    })
                    .catch(error => Observable.of(actionCreators.saveCompanyNumberingRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.SAVE_CANCELLED))
            )
        })

// Delete

const deleteCompanyNumberingEpic = (action$, store) =>
    action$
        .ofType(actionTypes.DELETE)
        .filter(() => !store.getState().companies.getIn(['deletion', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.DELETE_STARTED,
                }),
                request({
                    path: `company-numberings/${action.payload}`,
                    method: 'DELETE',
                })
                    .switchMap(() => Observable.of(actionCreators.deleteCompanyNumberingFulfilled(action.payload)))
                    .catch(error => Observable.of(actionCreators.deleteCompanyNumberingRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.DELETE_CANCELLED))
            )
        )

// Export

const exportCompanyNumberingsEpic = (action$, store) =>
    action$.ofType(actionTypes.EXPORT).switchMap(action => {
        const filters = JSON.stringify(action.payload.filters)
        const sorting = JSON.stringify(action.payload.sorting)
        const token = store.getState().auth.get('accessToken')

        new DownloadRequest({
            url: `company-numberings/export?filters=${filters}&sorting=${sorting}&token=${token}`,
        }).run()

        return Observable.concat(
            Observable.of({
                type: actionTypes.EXPORT_FULFILLED,
            })
        )
    })

export default [
    fetchCompanyNumberingsEpic,
    fetchCompanyNumberingEpic,
    saveCompanyNumberingEpic,
    deleteCompanyNumberingEpic,
    exportCompanyNumberingsEpic,
]
